import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LoanStatusTypeVersion2 } from 'pages/dashboard/home/dashboardService';
import { getLoanProgress } from 'pages/dashboard/store/dashboardSlice';
import { data } from './data';
import './milestoneCard.scss';
import doubleArrowIcon from '../../../assets/images/v2/double-arrow-vertical.svg';
import tickMark from '../../../assets/images/v2/tick-mark.svg';
import MilestoneUnkownErrorCircle from '../../../assets/images/v2/Milestone-Unknown-Error.svg';

interface MilestoneStepData {
  id: number;
  status: LoanStatusTypeVersion2;
  label: string;
  description: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

const MilestoneCard = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldBlink, setShouldBlink] = useState(false);

  const loanProgress = useSelector(getLoanProgress);
  const isValidStatus = (status: any): status is LoanStatusTypeVersion2 => {
    const validStatuses: LoanStatusTypeVersion2[] = ['Started', 'PreApproved', 'Processing', 'InitialApproval', 'ClearToClose', 'Closed', 'Unknown'];
    return validStatuses.includes(status as LoanStatusTypeVersion2);
  };

  const status: LoanStatusTypeVersion2 = loanProgress != undefined && isValidStatus(loanProgress?.status) ? loanProgress.status : 'Unknown';
  const isPreApproved: boolean = loanProgress?.isPreApproved ?? false;

  const applicableStatuses: LoanStatusTypeVersion2[] = useMemo(() => {
    if (isPreApproved) {
      return ['Started', 'PreApproved', 'Processing', 'InitialApproval', 'ClearToClose', 'Closed'];
    }
    return ['Started', 'Processing', 'InitialApproval', 'ClearToClose', 'Closed'];
  }, [isPreApproved]);

  const ProgressBarStates: Partial<Record<LoanStatusTypeVersion2, number>> = useMemo(() => {
    const baseStates = {
      Started: 1,
      Processing: isPreApproved ? 3 : 2,
      InitialApproval: isPreApproved ? 4 : 3,
      ClearToClose: isPreApproved ? 5 : 4,
      Closed: isPreApproved ? 6 : 5,
    };

    if (isPreApproved) {
      return {
        ...baseStates,
        PreApproved: 2,
      };
    }

    return baseStates;
  }, [isPreApproved]);

  const animationDistances: Partial<Record<LoanStatusTypeVersion2, number>> = useMemo(() => {
    const baseDistances = {
      Started: 50,
      Processing: isPreApproved ? 230 : 140,
      InitialApproval: isPreApproved ? 330 : 240,
      ClearToClose: isPreApproved ? 430 : 340,
      Closed: isPreApproved ? 530 : 440,
    };

    if (isPreApproved) {
      return {
        ...baseDistances,
        PreApproved: 140,
      };
    }

    return baseDistances;
  }, [isPreApproved]);

  const statusValue = ProgressBarStates[status] ?? 0;
  const animationDistance = animationDistances[status] ?? 0;
  const isStatusUnknown = status === "Unknown" || !loanProgress;

  const milestones: MilestoneStepData[] = useMemo(() => {
    return applicableStatuses.map((milestoneStatus, index) => {
      const statusOrder = ProgressBarStates[milestoneStatus];
      return {
        id: index + 1,
        status: milestoneStatus,
        label: data[milestoneStatus]?.label?.title || milestoneStatus,
        description: data[milestoneStatus]?.label?.description || `${milestoneStatus} stage`,
        isCompleted: statusOrder !== undefined && statusValue >= statusOrder,
        isCurrent: status === milestoneStatus
      };
    });
  }, [applicableStatuses, status, statusValue]);

  const currentMilestone = milestones.find(m => m.isCurrent);
  const progressPercentage = milestones.length > 1
    ? ((milestones.findIndex(m => m.isCurrent) + 1) / milestones.length) * 100
    : 0;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    const blinkDelay = 350;
    const blinkDuration = 3000;

    setTimeout(() => {
      setShouldBlink(true);
    }, blinkDelay);
    setTimeout(() => {
      setShouldBlink(false);
    }, blinkDuration);

  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  const renderMilestone = (milestone: MilestoneStepData, index: number, isCurrent = false, isUpcoming = false, showToggleIcon = false, showConnector = false) => {
    const showGlowEffect = isCurrent && shouldBlink && isExpanded;

    let stepClassName = 'milestone-card__content__step';
    if (isCurrent && shouldBlink && isExpanded) {
      stepClassName += ' milestone-card__content__step blink-row';
    }

    const baseIconClass = 'milestone-card__content__step__details__icon';
    let iconClassName = `${baseIconClass} ${baseIconClass}--completed`;
    if (isCurrent) {
      iconClassName = `${baseIconClass} ${baseIconClass}--current`;
    } else if (isUpcoming) {
      iconClassName = `${baseIconClass} ${baseIconClass}--upcoming`;
    }

    const baseTitleClass = 'milestone-card__content__step__details__info__title';
    let titleClassName = baseTitleClass;
    if (isUpcoming) {
      titleClassName = `${baseTitleClass} ${baseTitleClass}--upcoming`;
    }

    const baseDescriptionClass = 'milestone-card__content__step__details__info__description';
    let descriptionClassName = baseDescriptionClass;
    if (isUpcoming) {
      descriptionClassName = `${baseDescriptionClass} ${baseDescriptionClass}--upcoming`;
    }

    const baseToggleClass = 'milestone-card__content__step__toggle-icon';
    const toggleIconClassName = isExpanded
      ? `${baseToggleClass} ${baseToggleClass}--expanded`
      : baseToggleClass;

    const toggleAltText = isExpanded ? "Collapse Double arrow" : "Dropdown Double arrow";
    const toggleAriaLabel = isExpanded ? "Collapse Milestone Details" : "Expand Milestone Details";

    return (
      <div className={stepClassName} key={index}>
        <div className="milestone-card__content__step__details">
          <div className={iconClassName}>
            {isCurrent || isUpcoming ? milestone.id : <img src={tickMark} alt="Tick Mark" />}
          </div>
          <div className="milestone-card__content__step__details__info">
            <p className={titleClassName}>
              {milestone.label}
            </p>
            <p className={descriptionClassName}>
              {milestone.description}
            </p>
          </div>
        </div>
        {showToggleIcon && (
          <button type="button" className="milestone-card__content__step__toggle-btn" onClick={() => toggleExpanded()} onKeyDown={handleKeyDown} tabIndex={0} aria-expanded={isExpanded} aria-label={toggleAriaLabel}>
            <img
              src={doubleArrowIcon}
              alt={toggleAltText}
              data-testid="double-arrow-icon"
              className={toggleIconClassName}
            />
          </button>
        )}
        {showConnector && (
          <div className="milestone-card__content__step__connector"></div>
        )}
        
        {showGlowEffect && (
          <>
            {/* Layer 1: Multi-layered Glow (Inner Core) */}
            <div className="glow-layer glow-layer-1"></div>

            {/* Layer 2: Outer Glow Ring (Extended Aura) */}
            <div className="glow-layer glow-layer-2"></div>

            {/* Layer 3: Shimmer Effect (Moving Highlight) */}
            <div className="glow-layer glow-layer-3"></div>

            {/* Layer 4: Pulsing Border (Edge Highlight) */}
            <div className="glow-layer glow-layer-4"></div>
          </>
        )}
      </div>
    );
  };

  const currentOnlyClassName = isExpanded
    ? "milestone-card__content__step--current-hidden"
    : "milestone-card__content__step--current-only";

  const allStepsClassName = isExpanded
    ? "milestone-card__content__step--all-visible"
    : "milestone-card__content__step--all-hidden";

  const progressFillElement = isStatusUnknown ? (
    <div
      className='milestone-card__progress__track__fill--disabled'
      style={{ width: '100%' }}
    />
  ) : (
    <div
      className='milestone-card__progress__track__fill'
      style={{ width: `${progressPercentage}%` }}
    />
  );

  return (
    <div className='milestone-card'>
      <div className='milestone-card__progress'>
        <div className='milestone-card__progress__track'>
          {progressFillElement}
        </div>
      </div>
      {isStatusUnknown ? (
        <div className="milestone-card__content__error">
          <img
            src={MilestoneUnkownErrorCircle}
            alt="Loading Issues Icon"
            className="milestone-card__content__error__icon"
          />
          <div className="milestone-card__content__error__text">
            <p className="milestone-card__content__error__title">Loading Issues</p>
            <p className="milestone-card__content__error__description">
              We are having issues loading your loan status details. Please try again later.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div
            className={currentOnlyClassName}
            style={{ '--animation-distance': `${animationDistance}px` } as React.CSSProperties}
          >
            {currentMilestone && renderMilestone(currentMilestone, 0, true, false, true, false)}
          </div>

          <div className={allStepsClassName}>
            {milestones.map((milestone, index) => {
              const isFirstMilestone = index === 0;
              const isLastMilestone = index === milestones.length - 1;
              const showConnector = !isLastMilestone;

              if (milestone.isCurrent) {
                return renderMilestone(milestone, index, true, false, isFirstMilestone, showConnector);
              } else if (milestone.isCompleted) {
                return renderMilestone(milestone, index, false, false, isFirstMilestone, showConnector);
              } else {
                return renderMilestone(milestone, index, false, true, isFirstMilestone, showConnector);
              }
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default MilestoneCard;